import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getOutstandingDocuments, calculateAllocationComponents, updateReceiptAllocations } from '../../../../../redux/actions/SocietyPortal/ReceiptAction/ReceiptAction';
import { selectedSocietyStorer } from '../../../../../utils/webStorer';

const COMPONENT_LABELS = {
    PRINCIPAL: 'Principal',
    INTEREST: 'Interest',
    ARREAR: 'Arrear',
    DEBIT_NOTE_ARREAR: 'Debit Note Arrear',
};

const UpdateAllocationsModal = ({ receipt, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        outstandingInvoices, outstandingDebitNotes, outstandingLoading,
        allocationComponents, componentsLoading,
        updateAllocationsLoading,
    } = useSelector(state => state.ReceiptReducer || {});

    const [allocations, setAllocations] = useState([]);

    const receiptAmount = parseFloat(receipt?.amount || 0);
    const tdsDeducted = parseFloat(receipt?.tds_deducted || 0);
    // TDS was withheld by the member on top of the banked amount, so it settles dues too -
    // allocations are capped on the pair, not on what reached the bank.
    const totalSettled = receiptAmount + tdsDeducted;
    const flatId = receipt?.flat_id || receipt?.flat?.flat_id;
    const receiptId = receipt?.receipt_id;

    // Fetch outstanding documents with receipt_id for edit mode
    useEffect(() => {
        if (flatId && receiptId) {
            dispatch(getOutstandingDocuments({ flat_id: flatId, society_id: societyId, receipt_id: receiptId }));
        }
    }, [dispatch, societyId, flatId, receiptId]);

    // Pre-populate existing allocations
    useEffect(() => {
        const existing = receipt?.invoiceAllocations || [];
        if (existing.length > 0) {
            setAllocations(existing.map(a => ({
                target_type: a.invoice_id ? 'invoice' : 'debit_note',
                target_id: String(a.invoice_id || a.debit_note_id || ''),
                amount: String(a.amount || ''),
            })));
        }
    }, [receipt]);

    const formatCurrency = (amount) => `Rs ${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const addAllocation = (type) => {
        setAllocations(prev => [...prev, { target_type: type, target_id: '', amount: '' }]);
    };

    const updateAllocation = (index, field, value) => {
        setAllocations(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const removeAllocation = (index) => {
        setAllocations(prev => prev.filter((_, i) => i !== index));
    };

    const totalAllocated = allocations.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
    const unallocated = totalSettled - totalAllocated;
    const validAllocations = allocations.filter(a => a.target_id && parseFloat(a.amount) > 0);

    const handlePreviewComponents = () => {
        if (!validAllocations.length) return;
        dispatch(calculateAllocationComponents({
            society_id: societyId,
            receipt_id: receiptId,
            allocations: validAllocations.map(a => ({
                ...(a.target_type === 'invoice' ? { invoice_id: parseInt(a.target_id) } : { debit_note_id: parseInt(a.target_id) }),
                amount: parseFloat(a.amount),
            })),
        }));
    };

    // Build lookup map from allocationComponents
    const componentsMap = {};
    (allocationComponents || []).forEach(c => {
        const key = c.invoice_id ? `invoice_${c.invoice_id}` : `debit_note_${c.debit_note_id}`;
        componentsMap[key] = c.components || [];
    });

    const handleSave = () => {
        if (totalAllocated > totalSettled + 0.01) {
            alert(tdsDeducted > 0
                ? 'Total allocated amount cannot exceed receipt amount plus TDS deducted.'
                : 'Total allocated amount cannot exceed receipt amount.');
            return;
        }

        dispatch(updateReceiptAllocations({
            id: receiptId,
            allocations: validAllocations.map(a => ({
                ...(a.target_type === 'invoice' ? { invoice_id: parseInt(a.target_id) } : { debit_note_id: parseInt(a.target_id) }),
                amount: parseFloat(a.amount),
            })),
            onSuccess: () => {
                onSuccess?.();
                onClose();
            },
        }));
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
            <div className="tw-bg-white tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-3xl tw-max-h-[90vh] tw-overflow-hidden tw-flex tw-flex-col">
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-w-10 tw-h-10 tw-rounded-lg tw-bg-blue-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:format-list-bulleted" className="tw-text-white tw-text-xl" />
                        </div>
                        <div>
                            <h2 className="tw-text-lg tw-font-semibold tw-text-gray-800">Update Allocations</h2>
                            <p className="tw-text-xs tw-text-gray-500">{receipt?.receipt_number} &middot; {formatCurrency(receiptAmount)}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-400">
                        <Icon icon="mdi:close" className="tw-text-xl" />
                    </button>
                </div>

                {/* Body */}
                <div className="tw-flex-1 tw-overflow-y-auto tw-p-5 tw-space-y-4">
                    {/* Amount summary bar */}
                    <div className="tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-flex tw-items-center tw-justify-between tw-text-sm">
                        <div className="tw-flex tw-items-center tw-gap-4">
                            <span className="tw-text-gray-500">Receipt: <span className="tw-font-semibold tw-text-gray-800">{formatCurrency(receiptAmount)}</span></span>
                            {tdsDeducted > 0 && (
                                <>
                                    <span className="tw-text-gray-500">TDS: <span className="tw-font-semibold tw-text-indigo-600">{formatCurrency(tdsDeducted)}</span></span>
                                    <span className="tw-text-gray-500">Settled: <span className="tw-font-semibold tw-text-gray-800">{formatCurrency(totalSettled)}</span></span>
                                </>
                            )}
                            <span className="tw-text-gray-500">Allocated: <span className="tw-font-semibold tw-text-blue-600">{formatCurrency(totalAllocated)}</span></span>
                            <span className="tw-text-gray-500">
                                Remaining:{' '}
                                <span className={`tw-font-semibold ${unallocated < -0.01 ? 'tw-text-red-600' : unallocated > 0.01 ? 'tw-text-amber-600' : 'tw-text-emerald-600'}`}>
                                    {formatCurrency(unallocated)}
                                </span>
                            </span>
                        </div>
                        <span className={`tw-font-semibold ${unallocated < -0.01 ? 'tw-text-red-600' : unallocated > 0.01 ? 'tw-text-amber-600' : 'tw-text-emerald-600'}`}>
                            {unallocated < -0.01 ? 'Over-allocated' : unallocated > 0.01 ? `Unallocated: ${formatCurrency(unallocated)}` : 'Fully Allocated'}
                        </span>
                    </div>

                    {/* Add buttons */}
                    <div className="tw-flex tw-items-center tw-justify-between">
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wider">Allocation Rows</p>
                        <div className="tw-flex tw-gap-2">
                            <button type="button" onClick={() => addAllocation('invoice')} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-bg-blue-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-blue-700">
                                <Icon icon="mdi:plus" /> Invoice
                            </button>
                            <button type="button" onClick={() => addAllocation('debit_note')} className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-bg-orange-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium hover:tw-bg-orange-700">
                                <Icon icon="mdi:plus" /> Debit Note
                            </button>
                        </div>
                    </div>

                    {/* Loading state */}
                    {outstandingLoading && (
                        <div className="tw-text-center tw-py-8">
                            <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600 tw-mx-auto"></div>
                            <p className="tw-text-sm tw-text-gray-500 tw-mt-3">Loading outstanding documents...</p>
                        </div>
                    )}

                    {/* Allocation rows */}
                    {!outstandingLoading && allocations.length > 0 && (
                        <div className="tw-space-y-3">
                            {allocations.map((alloc, allocIdx) => {
                                const isInvoice = alloc.target_type === 'invoice';
                                const usedIds = allocations
                                    .filter((a, i) => i !== allocIdx && a.target_type === alloc.target_type && a.target_id)
                                    .map(a => a.target_id);
                                const targetOptions = (isInvoice ? (outstandingInvoices || []) : (outstandingDebitNotes || []))
                                    .filter(t => !usedIds.includes(String(isInvoice ? t.invoice_id : t.note_id)));
                                const selectedTarget = (isInvoice ? (outstandingInvoices || []) : (outstandingDebitNotes || []))
                                    .find(t => String(isInvoice ? t.invoice_id : t.note_id) === alloc.target_id);
                                const compKey = isInvoice ? `invoice_${alloc.target_id}` : `debit_note_${alloc.target_id}`;
                                const components = componentsMap[compKey];
                                const isCarriedForward = selectedTarget?.is_carried_forward === 1;

                                return (
                                    <div key={allocIdx} className={`tw-border tw-rounded-lg tw-overflow-hidden ${isCarriedForward ? 'tw-border-gray-300 tw-opacity-80' : isInvoice ? 'tw-border-blue-200' : 'tw-border-orange-200'}`}>
                                        {/* Row header */}
                                        <div className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 ${isCarriedForward ? 'tw-bg-gray-100' : isInvoice ? 'tw-bg-blue-50' : 'tw-bg-orange-50'}`}>
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <Icon icon={isInvoice ? 'mdi:file-document-outline' : 'mdi:note-minus-outline'} className={`tw-text-base ${isCarriedForward ? 'tw-text-gray-400' : isInvoice ? 'tw-text-blue-600' : 'tw-text-orange-600'}`} />
                                                <span className="tw-text-xs tw-font-semibold tw-text-gray-700">{isInvoice ? 'Invoice' : 'Debit Note'}</span>
                                                {alloc.amount && <span className="tw-text-xs tw-font-bold tw-text-gray-800 tw-ml-2">{formatCurrency(alloc.amount)}</span>}
                                                {isCarriedForward && (
                                                    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-bg-gray-200 tw-text-gray-600 tw-rounded-full tw-text-[10px] tw-font-semibold">
                                                        <Icon icon="mdi:lock-outline" className="tw-text-xs" /> Carried Forward
                                                    </span>
                                                )}
                                            </div>
                                            {!isCarriedForward && (
                                                <button type="button" onClick={() => removeAllocation(allocIdx)} className="tw-p-1 tw-rounded hover:tw-bg-red-100 tw-text-gray-400 hover:tw-text-red-600">
                                                    <Icon icon="mdi:close" />
                                                </button>
                                            )}
                                        </div>

                                        <div className="tw-p-3 tw-space-y-3">
                                            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                                                <div>
                                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">{isInvoice ? 'Select Invoice' : 'Select Debit Note'} *</label>
                                                    <select
                                                        value={alloc.target_id}
                                                        onChange={(e) => updateAllocation(allocIdx, 'target_id', e.target.value)}
                                                        disabled={isCarriedForward}
                                                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-cursor-not-allowed"
                                                    >
                                                        <option value="">{`Select ${isInvoice ? 'Invoice' : 'Debit Note'}`}</option>
                                                        {targetOptions.map(t => {
                                                            const id = isInvoice ? t.invoice_id : t.note_id;
                                                            const displayAmount = t.available_amount != null ? t.available_amount : t.balance_due;
                                                            return (
                                                                <option key={id} value={id}>
                                                                    {t.document_number} - Available: {formatCurrency(displayAmount)} ({t.status})
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Amount *</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0.01"
                                                        value={alloc.amount}
                                                        onChange={(e) => updateAllocation(allocIdx, 'amount', e.target.value)}
                                                        placeholder="0.00"
                                                        disabled={isCarriedForward}
                                                        className="tw-w-full tw-px-3 tw-py-2 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-text-right focus:tw-outline-none focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-cursor-not-allowed"
                                                    />
                                                </div>
                                            </div>

                                            {/* Selected target info.

                                                Total / Paid / Balance are the document's own saved figures,
                                                the same ones the invoice list shows, so Total - Paid = Balance.
                                                Two earlier versions read as wrong: showing the allocatable
                                                amount as "Available" next to the real Paid (5,900 / 4,000 /
                                                5,900), then hiding this receipt's share from Paid (5,900 /
                                                0 / 5,900 on a PARTIAL invoice). What can be allocated here -
                                                balance plus this receipt's own share, since it is being
                                                re-allocated - is spelled out on the line below instead. */}
                                            {selectedTarget && (() => {
                                                const total = parseFloat(selectedTarget.net_payable) || 0;
                                                const paid = parseFloat(selectedTarget.amount_paid) || 0;
                                                const balance = selectedTarget.balance_due != null
                                                    ? parseFloat(selectedTarget.balance_due) || 0
                                                    : Math.round((total - paid) * 100) / 100;
                                                const available = selectedTarget.available_amount != null
                                                    ? parseFloat(selectedTarget.available_amount) || 0
                                                    : balance;
                                                // Part of Paid that came from this receipt (0 until the receipt is cleared)
                                                const paidByThisReceipt = Math.round((available - balance) * 100) / 100;
                                                const ownAllocation = parseFloat(selectedTarget.existing_allocation) || 0;
                                                return (
                                                    <div className="tw-p-2.5 tw-bg-gray-50 tw-rounded-lg tw-text-xs">
                                                        <div className="tw-grid tw-grid-cols-3 tw-gap-2">
                                                            <div><span className="tw-text-gray-400">Total:</span> <span className="tw-font-semibold">{formatCurrency(total)}</span></div>
                                                            <div><span className="tw-text-gray-400">Paid:</span> <span className="tw-font-semibold">{formatCurrency(paid)}</span></div>
                                                            <div>
                                                                <span className="tw-text-gray-400">Balance:</span>{' '}
                                                                <span className="tw-font-semibold tw-text-red-600">{formatCurrency(balance)}</span>
                                                            </div>
                                                        </div>
                                                        {paidByThisReceipt > 0 ? (
                                                            <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                                                Paid includes {formatCurrency(paidByThisReceipt)} from this receipt, so up to {formatCurrency(available)} can be allocated here.
                                                            </p>
                                                        ) : ownAllocation > 0 && (
                                                            <p className="tw-text-[10px] tw-text-gray-500 tw-mt-1">
                                                                {formatCurrency(ownAllocation)} is currently allocated from this receipt.
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })()}

                                            {/* Component breakdown */}
                                            {components?.length > 0 && (
                                                <div className="tw-p-2.5 tw-bg-emerald-50 tw-border tw-border-emerald-200 tw-rounded-lg">
                                                    <p className="tw-text-[11px] tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wider tw-mb-1.5">Component Breakdown</p>
                                                    <div className="tw-flex tw-flex-wrap tw-gap-3">
                                                        {components.map((comp, ci) => (
                                                            <div key={ci} className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs">
                                                                <span className="tw-text-gray-500">{COMPONENT_LABELS[comp.component] || comp.component}:</span>
                                                                <span className="tw-font-semibold tw-text-gray-800">{formatCurrency(comp.amount)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Empty state */}
                    {!outstandingLoading && allocations.length === 0 && (
                        <div className="tw-text-center tw-py-8 tw-text-gray-400">
                            <Icon icon="mdi:receipt-text-outline" className="tw-text-4xl tw-mb-2" />
                            <p className="tw-text-sm">No allocations. Click above to allocate against invoices or debit notes.</p>
                        </div>
                    )}

                    {/* Preview button */}
                    {validAllocations.length > 0 && (
                        <div className="tw-flex tw-justify-end">
                            <button
                                type="button"
                                onClick={handlePreviewComponents}
                                disabled={componentsLoading}
                                className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-emerald-300 tw-rounded-lg tw-text-xs tw-font-medium hover:tw-bg-emerald-100 disabled:tw-opacity-50"
                            >
                                <Icon icon={componentsLoading ? "mdi:loading" : "mdi:calculator-variant-outline"} className={componentsLoading ? 'tw-animate-spin' : ''} />
                                {componentsLoading ? 'Calculating...' : 'Preview Component Breakdown'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-px-5 tw-py-4 tw-border-t tw-border-gray-100">
                    <button onClick={onClose} className="tw-px-4 tw-py-2 tw-bg-gray-200 tw-text-gray-700 tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-gray-300">
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={updateAllocationsLoading || unallocated < -0.01}
                        className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-bg-blue-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        <Icon icon={updateAllocationsLoading ? "mdi:loading" : "mdi:check"} className={updateAllocationsLoading ? 'tw-animate-spin' : ''} />
                        {updateAllocationsLoading ? 'Saving...' : 'Save Allocations'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpdateAllocationsModal;
